import { BENCHMARK_CONTRACTS, DEFAULT_BENCHMARK_SYMBOL } from "../../config/benchmarkContracts";
import {
  computeFactorPeriodReturn,
  getV3FilterWindowBounds,
  resolveLaunchClipMs,
} from "../../components/performanceChartV3Utils";
import { benchmarkFactorsService } from "../../services/benchmarkFactorsService";
import {
  discoverFactorsService,
  type DiscoverFactorsBatchRequest,
} from "../../services/discoverFactorsService";
import type { FactorEntry } from "../../services/productFactorsService";
import { productSearchService } from "../../services/productSearchService";
import type { ManagedStrategySearchItem } from "../../types/productV3";
import type { InvestorAlphaRecommendationId } from "../../types/investorAlpha";
import {
  computeCorrelationMatrix,
  factorsToDailyReturns,
} from "../../utils/riskMetrics";
import {
  buildAnalyzerWindowDraftStrategyFactors,
  buildWeightedDraftStrategyFactors,
  type DraftFactorProductInput,
} from "../../utils/strategyAnalyzerDraftFactors";
import { filterCompletedMarketDayFactors } from "../../utils/strategyAnalyzerMarketCalendar";
import {
  getManagedCurrentComposition,
  isValidManagedChild,
} from "../../utils/managedStrategyRollup";
import {
  computeFactorCumulativeReturn,
  filterFactorsForAnalyzerWindow,
  getCommonAnalyzerWindow,
  type AnalyzerWindow,
} from "../../utils/strategyAnalyzerPeriods";
import {
  resolveAlphaRiskScore,
  type AlphaRiskKey,
  type AlphaRiskTrack,
} from "./alphaRiskMap";
import { ALPHA_MANAGED_RECOMMENDATION_CONFIG } from "./alphaManagedStrategyConfig";
import { getAlphaManagedStrategyByMixId } from "./alphaManagedStrategyResolver";

export const ALPHA_PERFORMANCE_START_ISO = "2026-01-01T00:00:00.000Z";
const ALPHA_PERFORMANCE_START_MS = Date.parse(ALPHA_PERFORMANCE_START_ISO);

// Promo copy ("Since Jan 2026") must follow this window. UTC so a local
// timezone cannot shift the month on a midnight ISO start.
export function formatAlphaPerformanceSinceMonthYear(
  startIso: string = ALPHA_PERFORMANCE_START_ISO
): string {
  return new Date(startIso).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}
const FACTORS_PAGE_SIZE = 10_000;

// One validated leg of a managed strategy's activated composition, with the
// weight expressed in percent. Direction derives from the weight sign only
// and never from a text label.
export interface AlphaManagedCompositionEntry {
  entityType: "PORTFOLIO" | "BASKET";
  productId: number;
  providerId: number;
  weightPct: number;
}

type AlphaManagedFactorProduct = DraftFactorProductInput & {
  entityType: "PORTFOLIO" | "BASKET";
  productType: "portfolio" | "basket";
  providerId: number;
  productId: number;
};

export interface AlphaRecommendationData {
  managedStrategyId: number;
  managedStrategyTitle: string;
  mixId: string;
  riskTrack: AlphaRiskTrack;
  riskKeys: readonly AlphaRiskKey[];
  riskLevelLabel: string;
  window: AnalyzerWindow;
  strategyFactors: FactorEntry[];
  benchmarkFactors: FactorEntry[];
  strategyReturnPct: number;
  benchmarkReturnPct: number;
  highestPairCorrelationPct: number | null;
  composition: AlphaManagedCompositionEntry[];
}

export type AlphaManagedStrategyPerformanceData = Omit<
  AlphaRecommendationData,
  "mixId" | "riskTrack" | "riskKeys" | "riskLevelLabel"
>;

export interface AlphaInvestmentValuePoint {
  timestamp: number;
  value: number;
}

function parseTimestamp(value: string): number {
  const parsed = new Date(value).getTime();
  if (!Number.isFinite(parsed)) {
    throw new Error("Performance history contains an invalid date.");
  }
  return parsed;
}

function validateFactors(factors: FactorEntry[], label: string): FactorEntry[] {
  if (factors.length === 0) {
    throw new Error(`${label} has no performance history.`);
  }

  const validated = factors.map((factor) => {
    const numericFactor = Number(factor.f);
    parseTimestamp(factor.t);
    if (!Number.isFinite(numericFactor) || numericFactor <= 0) {
      throw new Error(`${label} has invalid performance history.`);
    }
    return { t: factor.t, f: numericFactor };
  });

  return validated.sort((left, right) =>
    parseTimestamp(left.t) - parseTimestamp(right.t)
  );
}

function toDailyFactors(factors: FactorEntry[]): FactorEntry[] {
  return factorsToDailyReturns(factors).map((point) => ({
    t: point.t,
    f: 1 + point.r,
  }));
}

function factorsOnOrAfterStart(factors: FactorEntry[]): FactorEntry[] {
  return factors.filter((factor) => parseTimestamp(factor.t) >= ALPHA_PERFORMANCE_START_MS);
}

function buildAlphaWindow(
  availabilityFactors: FactorEntry[],
  benchmarkFactors: FactorEntry[],
  requestedStartMs: number
): AnalyzerWindow {
  const commonWindow = getCommonAnalyzerWindow(
    [availabilityFactors, benchmarkFactors],
    "Since inception"
  );
  if (!commonWindow || commonWindow.startMs >= commonWindow.endMs) {
    throw new Error("The managed strategy does not have a shared performance window.");
  }

  return {
    ...commonWindow,
    requestedStartMs,
    isTruncated: commonWindow.startMs > requestedStartMs,
  };
}

function getHighestPairCorrelationPct(
  productFactors: Map<string, FactorEntry[]>
): number | null {
  if (productFactors.size < 2) return null;
  const correlation = computeCorrelationMatrix(productFactors);
  const values: number[] = [];

  for (let row = 0; row < correlation.products.length; row += 1) {
    for (let column = row + 1; column < correlation.products.length; column += 1) {
      const value = correlation.matrix[row]?.[column];
      if (value !== null && Number.isFinite(value)) values.push(value);
    }
  }

  if (values.length === 0) return null;
  return values.sort((left, right) => Math.abs(right) - Math.abs(left))[0] * 100;
}

function resolveManagedCompositionProducts(
  composition: unknown,
  emptyMessage: string
): AlphaManagedFactorProduct[] {
  if (!Array.isArray(composition) || composition.length === 0) {
    throw new Error(emptyMessage);
  }

  const seenKeys = new Set<string>();
  return composition.map((entry) => {
    if (!isValidManagedChild(entry)) {
      throw new Error("The managed strategy composition is invalid.");
    }

    const key = `${entry.entityType}-${entry.productId}`;
    if (seenKeys.has(key)) {
      throw new Error("The managed strategy composition contains duplicate products.");
    }
    seenKeys.add(key);

    return {
      key,
      name: key,
      weight: entry.weight,
      factors: [],
      entityType: entry.entityType,
      productType: entry.entityType === "PORTFOLIO" ? "portfolio" : "basket",
      providerId: entry.providerId,
      productId: entry.productId,
    };
  });
}

export function buildAlphaInvestmentValueSeries(
  factors: FactorEntry[],
  window: AnalyzerWindow,
  notional: number
): AlphaInvestmentValuePoint[] {
  const baselineMs = window.requestedStartMs ?? window.startMs;
  const points: AlphaInvestmentValuePoint[] = [
    { timestamp: baselineMs, value: notional },
  ];
  let value = notional;

  for (const factor of factors) {
    const timestamp = parseTimestamp(factor.t);
    if (timestamp < window.startMs || timestamp > window.endMs) continue;
    value *= Number(factor.f);
    points.push({
      timestamp: timestamp === baselineMs ? timestamp + 1 : timestamp,
      value,
    });
  }

  return points;
}

function readManagedStrategyMixId(
  managedStrategy: ManagedStrategySearchItem
): string | null {
  const mixId = "mixId" in managedStrategy.settings
    ? managedStrategy.settings.mixId
    : null;
  return typeof mixId === "string" && mixId.trim().length > 0
    ? mixId
    : null;
}

function getManagedStrategyMixId(
  managedStrategy: ManagedStrategySearchItem
): string {
  const mixId = readManagedStrategyMixId(managedStrategy);
  if (mixId === null) {
    throw new Error("The managed strategy does not have a valid mix ID.");
  }
  return mixId;
}

function resolveTeaserRiskFields(
  managedStrategy: ManagedStrategySearchItem,
  riskTrack: AlphaRiskTrack
): Pick<
  AlphaRecommendationData,
  "mixId" | "riskTrack" | "riskKeys" | "riskLevelLabel"
> {
  const mixId = readManagedStrategyMixId(managedStrategy);
  if (mixId === null) {
    return {
      mixId: "",
      riskTrack,
      riskKeys: [],
      riskLevelLabel: "--",
    };
  }

  try {
    const riskResolution = resolveAlphaRiskScore(riskTrack, mixId);
    return {
      mixId,
      riskTrack,
      riskKeys: riskResolution.riskKeys,
      riskLevelLabel: riskResolution.formattedScore,
    };
  } catch {
    return {
      mixId,
      riskTrack,
      riskKeys: [],
      riskLevelLabel: "--",
    };
  }
}

async function loadAlphaManagedStrategyPerformance(
  managedStrategy: ManagedStrategySearchItem
): Promise<AlphaManagedStrategyPerformanceData> {
  const managedStrategyId = managedStrategy.productId;
  const composition = managedStrategy.snapshot?.activated?.composition;
  const products = resolveManagedCompositionProducts(
    composition,
    "The managed strategy has no active product composition."
  );

  const benchmark = BENCHMARK_CONTRACTS.find(
    (candidate) => candidate.symbol === DEFAULT_BENCHMARK_SYMBOL
  );
  if (!benchmark) {
    throw new Error("The S&P 500 benchmark is not configured.");
  }

  const factorRequests: DiscoverFactorsBatchRequest[] = products.map((product) => ({
    providerId: product.providerId,
    productType: product.productType,
    productId: product.productId,
    pageSize: FACTORS_PAGE_SIZE,
  }));
  const [factorResults, rawBenchmarkFactors] = await Promise.all([
    discoverFactorsService.getFactorsBatch(factorRequests),
    benchmarkFactorsService.getContractFactors(benchmark.conId),
  ]);

  const factorProductHistories = products.map((product) => {
    const result = factorResults.find(
      (candidate) =>
        candidate.providerId === product.providerId
        && candidate.productType === product.productType
        && candidate.productId === product.productId
    );
    if (!result) {
      throw new Error(`${product.key} performance history was not returned.`);
    }

    const completedFactors = validateFactors(
      filterCompletedMarketDayFactors(
        validateFactors(result.factors, product.key)
      ),
      product.key
    );
    const dailyCompletedFactors = toDailyFactors(completedFactors);
    const factors = validateFactors(
      factorsOnOrAfterStart(completedFactors),
      product.key
    );
    return {
      firstAvailableMs: parseTimestamp(dailyCompletedFactors[0].t),
      product: {
        key: product.key,
        name: product.name,
        weight: product.weight,
        factors,
      } satisfies DraftFactorProductInput,
    };
  });

  const completedBenchmarkFactors = validateFactors(
    toDailyFactors(
      filterCompletedMarketDayFactors(
        validateFactors(rawBenchmarkFactors, benchmark.label)
      )
    ),
    benchmark.label
  );
  const benchmarkFactors = validateFactors(
    factorsOnOrAfterStart(completedBenchmarkFactors),
    benchmark.label
  );
  const factorProducts = factorProductHistories.map(({ product }) => product);
  const requestedStartMs = ALPHA_PERFORMANCE_START_MS;
  const availabilityFactors = validateFactors(
    filterCompletedMarketDayFactors(
      buildWeightedDraftStrategyFactors(factorProducts, "rebalanced")
    ),
    managedStrategy.title
  );
  const window = buildAlphaWindow(
    availabilityFactors,
    benchmarkFactors,
    requestedStartMs
  );
  const strategyFactors = validateFactors(
    filterFactorsForAnalyzerWindow(
      filterCompletedMarketDayFactors(
        buildAnalyzerWindowDraftStrategyFactors(factorProducts, "drifted", window)
      ),
      window
    ),
    managedStrategy.title
  );
  const windowedBenchmarkFactors = validateFactors(
    filterFactorsForAnalyzerWindow(benchmarkFactors, window),
    benchmark.label
  );
  const strategyReturnPct = computeFactorCumulativeReturn(strategyFactors);
  const benchmarkReturnPct = computeFactorCumulativeReturn(windowedBenchmarkFactors);
  if (strategyReturnPct === null || benchmarkReturnPct === null) {
    throw new Error("The managed strategy return could not be calculated.");
  }

  const windowedProductFactors = new Map(
    factorProducts.map((product) => [
      product.key,
      filterFactorsForAnalyzerWindow(toDailyFactors(product.factors), window),
    ])
  );

  return {
    managedStrategyId,
    managedStrategyTitle: managedStrategy.title,
    window,
    strategyFactors,
    benchmarkFactors: windowedBenchmarkFactors,
    strategyReturnPct,
    benchmarkReturnPct,
    highestPairCorrelationPct: getHighestPairCorrelationPct(windowedProductFactors),
    composition: products.map((product) => ({
      entityType: product.entityType,
      productId: product.productId,
      providerId: product.providerId,
      weightPct: product.weight,
    })),
  };
}

async function loadAlphaManagedStrategyData(
  managedStrategy: ManagedStrategySearchItem,
  riskTrack: AlphaRiskTrack,
  investmentAmountUsd?: number
): Promise<AlphaRecommendationData> {
  const mixId = getManagedStrategyMixId(managedStrategy);
  const riskResolution = resolveAlphaRiskScore(
    riskTrack,
    mixId,
    investmentAmountUsd
  );
  const performance = await loadAlphaManagedStrategyPerformance(
    managedStrategy
  );
  return {
    ...performance,
    mixId,
    riskTrack,
    riskKeys: riskResolution.riskKeys,
    riskLevelLabel: riskResolution.formattedScore,
  };
}

export async function loadAlphaManagedStrategyDataByMixId(
  mixId: string,
  riskTrack: AlphaRiskTrack,
  investmentAmountUsd: number,
  signal?: AbortSignal
): Promise<AlphaRecommendationData> {
  const managedStrategy = await getAlphaManagedStrategyByMixId(mixId, signal);
  return loadAlphaManagedStrategyData(
    managedStrategy,
    riskTrack,
    investmentAmountUsd
  );
}

export async function loadAlphaManagedStrategyDataByProductId(
  managedStrategyId: number,
  signal?: AbortSignal
): Promise<AlphaManagedStrategyPerformanceData> {
  const managedStrategy = await productSearchService.getManagedStrategyForInvestor(
    managedStrategyId,
    signal
  );
  return loadAlphaManagedStrategyPerformance(managedStrategy);
}

/**
 * Computes the current managed-model Today return from its current signed
 * child weights and the same strict local-midnight factor window as the Alpha
 * dashboard. This path intentionally does not use the Analyzer performance
 * loader, which excludes the in-progress market day by design.
 */
export async function loadAlphaManagedStrategyTodayReturnByProductId(
  managedStrategyId: number,
  entityCreateDate: string | null = null,
  signal?: AbortSignal
): Promise<number | null> {
  const managedStrategy = await productSearchService.getManagedStrategyForInvestor(
    managedStrategyId,
    signal
  );
  if (managedStrategy.snapshot?.partial === true) {
    throw new Error("The managed strategy composition is incomplete.");
  }

  const products = resolveManagedCompositionProducts(
    getManagedCurrentComposition(managedStrategy),
    "The managed strategy has no current product composition."
  ).filter((product) => product.weight !== 0);
  if (products.length === 0) return null;

  const now = new Date();
  const entityCreateMs = resolveLaunchClipMs(entityCreateDate, now.getTime());
  const effectiveEntityCreateDate = entityCreateMs === null
    ? null
    : new Date(entityCreateMs).toISOString();
  const { start, end } = getV3FilterWindowBounds(
    "Today",
    now,
    effectiveEntityCreateDate
  );
  const requests: DiscoverFactorsBatchRequest[] = products.map((product) => ({
    providerId: product.providerId,
    productType: product.productType,
    productId: product.productId,
    from: start?.toISOString(),
    to: end.toISOString(),
    pageSize: FACTORS_PAGE_SIZE,
  }));
  const results = await discoverFactorsService.getFactorsBatch(requests);

  let managedReturnPct = 0;
  for (const product of products) {
    const result = results.find(
      (candidate) =>
        candidate.providerId === product.providerId
        && candidate.productType === product.productType
        && candidate.productId === product.productId
    );
    if (!result || result.failed) {
      throw new Error(`${product.key} Today performance could not be loaded.`);
    }
    if (result.factors.length === 0) return null;

    const productReturnPct = computeFactorPeriodReturn(
      validateFactors(result.factors, product.key),
      "Today",
      effectiveEntityCreateDate
    );
    if (productReturnPct === null) return null;
    managedReturnPct += (product.weight / 100) * productReturnPct;
  }

  return managedReturnPct;
}

export async function loadAlphaRecommendationData(
  recommendationId: InvestorAlphaRecommendationId,
  signal?: AbortSignal
): Promise<AlphaRecommendationData> {
  const config = ALPHA_MANAGED_RECOMMENDATION_CONFIG[recommendationId];
  const managedStrategy = await productSearchService.getManagedStrategyForInvestor(
    config.productId,
    signal
  );
  const performance = await loadAlphaManagedStrategyPerformance(managedStrategy);
  return {
    ...performance,
    ...resolveTeaserRiskFields(managedStrategy, config.riskTrack),
  };
}
